import React, { useState, ReactNode, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { isMockMode, resetMockDatabase, getCrecheSelecionada, setCrecheSelecionada } from '../services/api';
import { podeAcessarRota, ehAdminSistema } from '../auth/perfil-acesso';
import { usePlano } from '../hooks/usePlano';
import { useNotificacoes } from '../hooks/useNotificacoes';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  planoMinimo?: 'BRONZE' | 'PRATA' | 'OURO';
}

const NAV_ITEMS: NavItem[] = [
  { to: '/app/meu-filho',      label: 'Meu Filho',         icon: '🧒', planoMinimo: 'BRONZE' }, // Portal dos Pais (responsável)
  { to: '/app/sistema',        label: 'Gestão SaaS',       icon: '🧭', planoMinimo: 'OURO' }, // Somente Admin SaaS
  { to: '/app',                label: 'Dashboard',         icon: '📊', planoMinimo: 'BRONZE' },
  { to: '/app/creches',        label: 'Unidades',          icon: '🏫', planoMinimo: 'OURO' }, 
  { to: '/app/alunos',         label: 'Crianças',          icon: '👶', planoMinimo: 'BRONZE' },
  { to: '/app/familias',       label: 'Famílias',          icon: '👨‍👩‍👧', planoMinimo: 'BRONZE' },
  // "Funcionários" precisa estar NO NOME: é o termo que o resto do sistema usa
  // (a tela se chama "Funcionários — Cadastro Completo", e o Meu Painel manda
  // "cadastrar no cadastro de funcionários"). Chamado só de "Módulo RH", o item
  // não era encontrado por quem seguia essa instrução — aconteceu de verdade.
  { to: '/app/funcionarios',   label: 'Funcionários & RH', icon: '🗂️', planoMinimo: 'PRATA' },
  { to: '/app/pedagogico',     label: 'Pedagógico',        icon: '📚', planoMinimo: 'OURO' },
  { to: '/app/administrativo', label: 'Administrativo',    icon: '🏢', planoMinimo: 'PRATA' },
  // Ouro porque é o plano em que o card da landing page vende Infraestrutura.
  { to: '/app/infraestrutura', label: 'Infraestrutura',    icon: '🏗️', planoMinimo: 'OURO' },
  { to: '/app/seguranca',      label: 'Segurança',         icon: '🛡️', planoMinimo: 'OURO' },
  { to: '/app/ia',             label: 'Assistente Virtual', icon: '🤖', planoMinimo: 'OURO' },
  { to: '/app/relatorios',     label: 'BI & Relatórios',   icon: '🧾', planoMinimo: 'OURO' },
  // A sala vem ANTES da turma: é dela que a turma é montada.
  { to: '/app/salas',          label: 'Salas',             icon: '🏫', planoMinimo: 'BRONZE' },
  { to: '/app/turmas',         label: 'Turmas',            icon: '🎓', planoMinimo: 'BRONZE' },
  { to: '/app/demanda',        label: 'Demanda & Matrículas', icon: '📝', planoMinimo: 'BRONZE' },
  { to: '/app/visitas',        label: 'Visitas & Captação', icon: '🤝', planoMinimo: 'BRONZE' },
  { to: '/app/pagina-publica', label: 'Página Pública',    icon: '🌐', planoMinimo: 'BRONZE' },
  { to: '/app/secretaria',     label: 'Secretaria & Termos', icon: '📄', planoMinimo: 'BRONZE' },
  { to: '/app/financeiro',     label: 'Financeiro',        icon: '💰', planoMinimo: 'PRATA' },
  { to: '/app/cardapios',      label: 'Cardápios',         icon: '🍽️', planoMinimo: 'PRATA' },
  { to: '/app/nutricao',       label: 'Despensa & Aceitação', icon: '🥫', planoMinimo: 'PRATA' },
  { to: '/app/calendario',     label: 'Calendário',        icon: '📅', planoMinimo: 'BRONZE' },
  { to: '/app/comunicacao',    label: 'Comunicação',       icon: '💬', planoMinimo: 'BRONZE' },
  // Prata porque é o plano em que o card da landing page vende o AEE/Inclusão.
  { to: '/app/atendimentos',   label: 'Inclusão',          icon: '♿', planoMinimo: 'PRATA' },
  { to: '/app/saude',          label: 'Saúde',             icon: '🩺', planoMinimo: 'PRATA' },
  { to: '/app/rotina',         label: 'Rotina Diária',     icon: '📋', planoMinimo: 'BRONZE' },
  { to: '/app/presenca',       label: 'Presença',          icon: '🗓️', planoMinimo: 'BRONZE' },
  { to: '/app/meu-painel',     label: 'Meu Painel',        icon: '🧑‍🏫', planoMinimo: 'BRONZE' }, // Portal do colaborador
  { to: '/app/usuarios',       label: 'Acesso Usuarios',   icon: '👤', planoMinimo: 'BRONZE' },
  { to: '/app/importar',       label: 'Importar Dados',    icon: '📥', planoMinimo: 'BRONZE' },
];

interface CrecheOpcao {
  id: number;
  nome: string;
}

export default function Layout({ children }: { children: ReactNode }) {
  const { usuario, logout } = useAuth();
  const [sidebarAberta, setSidebarAberta] = useState(true);
  const [resetFeedback, setResetFeedback] = useState('');
  const [painelAberto, setPainelAberto] = useState(false);
  const [crechesDisponiveis, setCrechesDisponiveis] = useState<CrecheOpcao[]>([]);
  const [crecheSelecionadaId, setCrecheSelecionadaId] = useState(() => getCrecheSelecionada());
  const navigate = useNavigate();
  const ehAdmin = ehAdminSistema(usuario?.tipo_usuario);

  // Plano vindo da ASSINATURA da creche — ver `usePlano`.
  const { alcanca } = usePlano();

  useEffect(() => {
    if (!ehAdmin) return;
    let ativo = true;
    api.get<CrecheOpcao[]>('/creches').then(({ data }) => {
      if (ativo) setCrechesDisponiveis(Array.isArray(data) ? data : []);
    }).catch(() => {
      if (ativo) setCrechesDisponiveis([]);
    });
    return () => { ativo = false; };
  }, [ehAdmin]);

  function handleTrocarCreche(idCreche: number) {
    setCrecheSelecionada(idCreche || null);
    setCrecheSelecionadaId(idCreche);
    window.location.reload();
  }

  const navItemsVisiveis = NAV_ITEMS.filter((item) => {
    // 1. Filtro por Perfil (Role)
    const roleOk = podeAcessarRota(usuario?.tipo_usuario, item.to);
    if (!roleOk) return false;

    // 2. Filtro por Plano SaaS
    //
    // Este bloco lia `usuario.id_plano` — campo que NUNCA existiu, nem na
    // entidade nem no token. `undefined` caía no default e TODA creche era
    // tratada como Bronze: quem pagava Prata não via Funcionários, Financeiro,
    // Cardápios, Saúde nem Nutrição. E a exceção testava `'ADMIN'`, valor que o
    // sistema não usa (o tipo real é `super_admin`), então nem o admin escapava.
    //
    // O plano vive na ASSINATURA da creche e agora vem de `/saas/meu-plano`.
    if (ehAdmin) return true;
    return alcanca(item.planoMinimo);
  });
  const { notificacoes, totalNaoLidas, marcarComoLida, marcarTodasComoLidas } = useNotificacoes();
  const painelRef = useRef<HTMLDivElement>(null);

  // Fecha painel ao clicar fora
  useEffect(() => {
    function handleClickFora(e: MouseEvent) {
      if (painelRef.current && !painelRef.current.contains(e.target as Node)) {
        setPainelAberto(false);
      }
    }
    if (painelAberto) document.addEventListener('mousedown', handleClickFora);
    return () => document.removeEventListener('mousedown', handleClickFora);
  }, [painelAberto]);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  function handleResetDemoData() {
    const confirmed = window.confirm('Resetar dados de demonstração? Isso apaga alterações feitas no modo mock.');
    if (!confirmed) return;
    resetMockDatabase();
    setResetFeedback('Dados de demonstração resetados com sucesso.');
    setTimeout(() => {
      window.location.reload();
    }, 450);
  }

  return (
    <div className={`layout ${sidebarAberta ? 'sidebar-aberta' : 'sidebar-fechada'}`}>
      <aside className="sidebar">
        <div className="sidebar-header">
          {sidebarAberta && <span className="sidebar-titulo">Creches</span>}
          <button
            className="btn-toggle-sidebar"
            onClick={() => setSidebarAberta(!sidebarAberta)}
            title={sidebarAberta ? 'Recolher menu' : 'Expandir menu'}
          >
            {sidebarAberta ? '◀' : '▶'}
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItemsVisiveis.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/app'}
              className={({ isActive }) => `nav-item${isActive ? ' ativo' : ''}`}
              title={!sidebarAberta ? item.label : undefined}
            >
              <span className="nav-icon">{item.icon}</span>
              {sidebarAberta && <span className="nav-label">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          {sidebarAberta && (
            <span className="usuario-email" title={usuario?.email}>
              {usuario?.email}
            </span>
          )}
          <button className="btn-logout" onClick={handleLogout} title="Sair">
            🚪
          </button>
        </div>
      </aside>

      <div className="conteudo">
        <header className="topbar">
          <div className="topbar-info">
            {isMockMode && (
              <>
                <span className="badge-modo-mock">Modo demonstração</span>
                <span className="badge-lgpd-demo">Ambiente sem dados reais de clientes (LGPD)</span>
              </>
            )}
            {resetFeedback && <span className="topbar-feedback">{resetFeedback}</span>}
          </div>
          <div className="topbar-actions">
            {ehAdmin && (
              <select
                className="seletor-creche"
                value={crecheSelecionadaId || ''}
                onChange={(e) => handleTrocarCreche(Number(e.target.value) || 0)}
                title="Operar módulos como esta creche"
                aria-label="Seletor de creche"
              >
                <option value="">— Selecione uma creche —</option>
                {crechesDisponiveis.map((creche) => (
                  <option key={creche.id} value={creche.id}>
                    {creche.nome}
                  </option>
                ))}
              </select>
            )}
            {/* Sino de notificações */}
            <div className="notificacoes-wrapper" ref={painelRef}>
              <button
                className="btn-sino"
                onClick={() => setPainelAberto((v) => !v)}
                title="Notificações"
                aria-label={`${totalNaoLidas} notificações não lidas`}
              >
                🔔
                {totalNaoLidas > 0 && (
                  <span className="sino-badge">{totalNaoLidas > 99 ? '99+' : totalNaoLidas}</span>
                )}
              </button>
              {painelAberto && (
                <div className="notificacoes-painel">
                  <div className="notificacoes-header">
                    <strong>Notificações</strong>
                    {totalNaoLidas > 0 && (
                      <button className="btn-marcar-todas" onClick={marcarTodasComoLidas}>
                        Marcar todas como lidas
                      </button>
                    )}
                  </div>
                  <ul className="notificacoes-lista">
                    {notificacoes.length === 0 && (
                      <li className="notificacao-vazia">Nenhuma notificação.</li>
                    )}
                    {notificacoes.slice(0, 20).map((n) => (
                      <li
                        key={n.id}
                        className={`notificacao-item${n.lida ? ' lida' : ''}`}
                        onClick={() => !n.lida && marcarComoLida(n.id)}
                      >
                        <div className="notificacao-titulo">{n.titulo}</div>
                        <div className="notificacao-mensagem">{n.mensagem}</div>
                        <div className="notificacao-data">
                          {new Date(n.criado_em).toLocaleString('pt-BR')}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <button className="btn-secundario" onClick={() => navigate('/')}>
              Ver home pública
            </button>
            {isMockMode && (
              <button className="btn-reset-demo" onClick={handleResetDemoData}>
                Resetar dados de demonstração
              </button>
            )}
          </div>
        </header>
        <main className="pagina">{children}</main>
      </div>
    </div>
  );
}
